import type {
  AcaoContextual,
  ContinuarFluxoDestino,
  ContinuarFluxoResult,
  PedidoOperacionalInput,
  ProximaEtapaResult,
  StatusFaturamento,
  StatusLogistico,
  StatusPedidoOperacional,
  StatusRetirada,
  StatusSeparacao,
  TimelineOperacionalItem,
  TipoEntrega,
} from "interfaces/loja/pedidoOperacional";

/** Rota da tela de faturamento do pedido (Fase 2). */
export function pedidoFaturamentoPath(pedidoId: string) {
  return `/loja/pedidos/${pedidoId}/faturamento`;
}

/** Rota da tela de separação do pedido (Fase 3). */
export function pedidoSeparacaoPath(pedidoId: string) {
  return `/loja/pedidos/${pedidoId}/separacao`;
}

/** Volta ao detalhe do pedido com âncora (ex.: retirada na página mãe). */
export function pedidoDetalheComAnchor(pedidoId: string, anchorId: string) {
  return `/loja/pedidos/${pedidoId}#${anchorId}`;
}

/** Rota da tela de expedição (Fase 4) — só aplica a `tipoEntrega = ENTREGA`. */
export function pedidoExpedicaoPath(pedidoId: string) {
  return `/loja/pedidos/${pedidoId}/expedicao`;
}

/** Rota da tela de retirada (Fase 5) — `RETIRADA_LOJA` ou `RETIRADA_EVENTO`. */
export function pedidoRetiradaPath(pedidoId: string) {
  return `/loja/pedidos/${pedidoId}/retirada`;
}

export function normalizeStatusPedido(
  raw: string | undefined | null
): StatusPedidoOperacional {
  const s = String(raw ?? "").toUpperCase();
  if (s === "PROCESSANDO") return "EM_PROCESSAMENTO";
  // Legado: enum antigo do pedido (migrado no banco para CANCELADO)
  if (s === "ESTORNADO") return "CANCELADO";
  return s as StatusPedidoOperacional;
}

function isPagamentoConfirmado(input: PedidoOperacionalInput): boolean {
  const st = normalizeStatusPedido(input.status);
  if (
    st === "PAGO" ||
    st === "EM_PROCESSAMENTO" ||
    st === "PROCESSANDO" ||
    st === "ENVIADO" ||
    st === "ENTREGUE"
  ) {
    return true;
  }
  const pags = input.pagamentos ?? [];
  return pags.some((p) => p.status === "PAGO");
}

function inferTipoEntrega(input: PedidoOperacionalInput): TipoEntrega {
  const t = input.tipoEntrega as TipoEntrega | undefined | null;
  if (t === "DIGITAL") return "DIGITAL";
  if (t === "ENTREGA" || t === "RETIRADA_LOJA" || t === "RETIRADA_EVENTO") {
    return t;
  }
  if (input.enderecoEntrega && typeof input.enderecoEntrega === "object") {
    return "ENTREGA";
  }
  if (input.enderecoEntregaId) {
    return "ENTREGA";
  }
  return "ENTREGA";
}

function inferStatusFaturamento(
  input: PedidoOperacionalInput
): StatusFaturamento {
  const v = input.statusFaturamento as StatusFaturamento | undefined | null;
  if (
    v === "PENDENTE" ||
    v === "EMITIDO" ||
    v === "CANCELADO" ||
    v === "ERRO"
  ) {
    return v;
  }
  const st = normalizeStatusPedido(input.status);
  if (st === "AGUARDANDO_PAGAMENTO") return "PENDENTE";
  return "PENDENTE";
}

function inferStatusSeparacao(input: PedidoOperacionalInput): StatusSeparacao {
  const v = input.statusSeparacao as StatusSeparacao | undefined | null;
  if (
    v === "PENDENTE" ||
    v === "EM_SEPARACAO" ||
    v === "SEPARADO" ||
    v === "DIVERGENCIA" ||
    v === "FALTA_ESTOQUE" ||
    v === "CANCELADA"
  ) {
    return v;
  }
  return "PENDENTE";
}

function inferStatusLogistico(input: PedidoOperacionalInput): StatusLogistico {
  const v = input.statusLogistico as StatusLogistico | undefined | null;
  if (
    v === "PENDENTE" ||
    v === "AGUARDANDO_ENVIO" ||
    v === "ETIQUETA_GERADA" ||
    v === "POSTADO" ||
    v === "EM_TRANSITO" ||
    v === "ENTREGUE" ||
    v === "DEVOLVIDO" ||
    v === "EXTRAVIADO"
  ) {
    return v;
  }
  return "PENDENTE";
}

function inferStatusRetirada(
  input: PedidoOperacionalInput,
  tipo: TipoEntrega
): StatusRetirada | null {
  if (tipo === "ENTREGA") return null;
  const v = input.statusRetirada as StatusRetirada | undefined | null;
  if (
    v === "PENDENTE" ||
    v === "PRONTO_PARA_RETIRADA" ||
    v === "RETIRADO" ||
    v === "ENTREGUE_NO_EVENTO" ||
    v === "NAO_RETIRADO" ||
    v === "CANCELADA"
  ) {
    return v;
  }
  return "PENDENTE";
}

/** Estado derivado único para UI e próxima etapa (sem duplicar regras nas telas). */
export function resolvePedidoOperacional(input: PedidoOperacionalInput) {
  const status = normalizeStatusPedido(input.status);
  const tipoEntrega = inferTipoEntrega(input);
  const statusFaturamento = inferStatusFaturamento(input);
  const statusSeparacao = inferStatusSeparacao(input);
  const statusLogistico = inferStatusLogistico(input);
  const statusRetirada = inferStatusRetirada(input, tipoEntrega);
  const pagamentoOk = isPagamentoConfirmado(input);

  return {
    status,
    tipoEntrega,
    statusFaturamento,
    statusSeparacao,
    statusLogistico,
    statusRetirada,
    pagamentoOk,
  };
}

/**
 * Define a próxima etapa operacional com base nos status do pedido.
 * Campos ausentes na API são inferidos de forma conservadora (ver resolvePedidoOperacional).
 */
export function getProximaEtapa(input: PedidoOperacionalInput): ProximaEtapaResult {
  const o = resolvePedidoOperacional(input);

  if (o.status === "CANCELADO") {
    return {
      titulo: "Pedido cancelado",
      descricao: "Não há etapas operacionais pendentes para este pedido.",
      prioridade: "baixa",
      categoria: "concluido",
    };
  }
  if (o.status === "DEVOLVIDO") {
    return {
      titulo: "Pedido devolvido",
      descricao: "Fluxo operacional encerrado após devolução.",
      prioridade: "baixa",
      categoria: "concluido",
    };
  }
  if (o.status === "DEVOLUCAO_SOLICITADA") {
    return {
      titulo: "Devolução solicitada",
      descricao: "Há solicitação de devolução — conclua o fluxo de análise e logística reversa.",
      prioridade: "alta",
      categoria: "atencao",
    };
  }

  if (o.status === "AGUARDANDO_PAGAMENTO" || !o.pagamentoOk) {
    return {
      titulo: "Aguardando pagamento",
      descricao: "Confirme o pagamento para liberar faturamento e separação.",
      prioridade: "alta",
      categoria: "pagamento",
      anchorId: "sec-pagamento",
    };
  }

  if (o.tipoEntrega === "DIGITAL") {
    if (o.status === "ENTREGUE") {
      return {
        titulo: "Entrega digital concluída",
        descricao: "Download disponível ao cliente em Minhas fotos.",
        prioridade: "baixa",
        categoria: "concluido",
      };
    }
    return {
      titulo: "Concluindo entrega digital",
      descricao: "Pedido digital — conclusão automática após pagamento.",
      prioridade: "media",
      categoria: "neutro",
    };
  }

  if (o.statusFaturamento === "ERRO") {
    return {
      titulo: "Corrigir faturamento",
      descricao: "Faturamento com erro — revise e emita novamente.",
      prioridade: "alta",
      categoria: "faturamento",
      routeTo: input.pedidoId ? pedidoFaturamentoPath(input.pedidoId) : undefined,
      anchorId: input.pedidoId ? undefined : "sec-faturamento",
    };
  }
  if (o.statusFaturamento === "PENDENTE") {
    return {
      titulo: "Emitir faturamento",
      descricao: "Após o pagamento confirmado, emita o documento fiscal.",
      prioridade: "alta",
      categoria: "faturamento",
      routeTo: input.pedidoId ? pedidoFaturamentoPath(input.pedidoId) : undefined,
      anchorId: input.pedidoId ? undefined : "sec-faturamento",
    };
  }
  if (o.statusFaturamento === "CANCELADO") {
    return {
      titulo: "Faturamento cancelado",
      descricao: "Verifique o pedido antes de prosseguir com separação ou envio.",
      prioridade: "media",
      categoria: "atencao",
      routeTo: input.pedidoId ? pedidoFaturamentoPath(input.pedidoId) : undefined,
      anchorId: input.pedidoId ? undefined : "sec-faturamento",
    };
  }

  const rotaSeparacao =
    input.pedidoId != null
      ? pedidoSeparacaoPath(input.pedidoId)
      : undefined;

  if (o.statusSeparacao === "DIVERGENCIA" || o.statusSeparacao === "FALTA_ESTOQUE") {
    return {
      titulo: "Tratar separação",
      descricao:
        o.statusSeparacao === "DIVERGENCIA"
          ? "Há divergência na separação — ajuste antes de seguir."
          : "Falta de estoque na separação — trate o estoque ou cancele itens.",
      prioridade: "alta",
      categoria: "separacao",
      routeTo: rotaSeparacao,
      anchorId: rotaSeparacao ? undefined : "sec-separacao",
    };
  }
  if (o.statusSeparacao === "PENDENTE") {
    return {
      titulo: "Iniciar separação",
      descricao: "O faturamento está emitido; inicie a separação dos itens.",
      prioridade: "alta",
      categoria: "separacao",
      routeTo: rotaSeparacao,
      anchorId: rotaSeparacao ? undefined : "sec-separacao",
    };
  }
  if (o.statusSeparacao === "EM_SEPARACAO") {
    return {
      titulo: "Finalizar separação",
      descricao: "Conclua a separação para liberar logística ou retirada.",
      prioridade: "alta",
      categoria: "separacao",
      routeTo: rotaSeparacao,
      anchorId: rotaSeparacao ? undefined : "sec-separacao",
    };
  }

  const rotaExpedicao =
    input.pedidoId != null
      ? pedidoExpedicaoPath(input.pedidoId)
      : undefined;
  const rotaRetirada =
    input.pedidoId != null ? pedidoRetiradaPath(input.pedidoId) : undefined;

  if (o.tipoEntrega === "ENTREGA") {
    if (o.statusLogistico === "ENTREGUE" || o.status === "ENTREGUE") {
      return {
        titulo: "Entrega concluída",
        descricao: "Pedido entregue ao destinatário.",
        prioridade: "baixa",
        categoria: "concluido",
      };
    }
    if (
      o.statusLogistico === "PENDENTE" ||
      o.statusLogistico === "AGUARDANDO_ENVIO"
    ) {
      return {
        titulo: "Expedição",
        descricao: "Gere a etiqueta e processe o envio do pedido.",
        prioridade: "alta",
        categoria: "logistica",
        routeTo: rotaExpedicao,
        anchorId: rotaExpedicao ? undefined : "sec-expedicao",
      };
    }
    if (o.statusLogistico === "ETIQUETA_GERADA") {
      return {
        titulo: "Postar envio",
        descricao: "Etiqueta gerada — registre a postagem quando despachar.",
        prioridade: "alta",
        categoria: "logistica",
        routeTo: rotaExpedicao,
        anchorId: rotaExpedicao ? undefined : "sec-expedicao",
      };
    }
    if (o.statusLogistico === "POSTADO" || o.statusLogistico === "EM_TRANSITO") {
      return {
        titulo: "Acompanhar entrega",
        descricao: "Pedido em trânsito — acompanhe até a entrega.",
        prioridade: "media",
        categoria: "logistica",
        routeTo: rotaExpedicao,
        anchorId: rotaExpedicao ? undefined : "sec-expedicao",
      };
    }
    return {
      titulo: "Logística",
      descricao: "Verifique o status logístico e eventos de envio.",
      prioridade: "media",
      categoria: "logistica",
      routeTo: rotaExpedicao,
      anchorId: rotaExpedicao ? undefined : "sec-expedicao",
    };
  }

  if (o.tipoEntrega === "RETIRADA_LOJA") {
    if (o.statusRetirada === "RETIRADO") {
      return {
        titulo: "Retirada concluída",
        descricao: "Cliente retirou o pedido na loja.",
        prioridade: "baixa",
        categoria: "concluido",
      };
    }
    if (
      o.statusRetirada === "PENDENTE" ||
      o.statusRetirada === null
    ) {
      return {
        titulo: "Preparar retirada",
        descricao: "Separação ok — marque quando estiver pronto para retirada na loja.",
        prioridade: "alta",
        categoria: "retirada",
        routeTo: rotaRetirada,
        anchorId: rotaRetirada ? undefined : "sec-retirada",
      };
    }
    if (o.statusRetirada === "PRONTO_PARA_RETIRADA") {
      return {
        titulo: "Confirmar retirada",
        descricao: "Aguardando o cliente retirar o pedido na loja.",
        prioridade: "media",
        categoria: "retirada",
        routeTo: rotaRetirada,
        anchorId: rotaRetirada ? undefined : "sec-retirada",
      };
    }
  }

  if (o.tipoEntrega === "RETIRADA_EVENTO") {
    if (o.statusRetirada === "ENTREGUE_NO_EVENTO") {
      return {
        titulo: "Entrega no evento concluída",
        descricao: "Pedido entregue no evento.",
        prioridade: "baixa",
        categoria: "concluido",
      };
    }
    if (o.statusRetirada === "PENDENTE" || o.statusRetirada === null) {
      return {
        titulo: "Preparar para o evento",
        descricao: "Separação ok — prepare a entrega no evento.",
        prioridade: "alta",
        categoria: "retirada",
        routeTo: rotaRetirada,
        anchorId: rotaRetirada ? undefined : "sec-retirada",
      };
    }
    if (o.statusRetirada === "PRONTO_PARA_RETIRADA") {
      return {
        titulo: "Entrega no evento",
        descricao: "Confirme a entrega ao participante no evento.",
        prioridade: "alta",
        categoria: "retirada",
        routeTo: rotaRetirada,
        anchorId: rotaRetirada ? undefined : "sec-retirada",
      };
    }
  }

  return {
    titulo: "Acompanhar pedido",
    descricao: "Use os cards abaixo para ver o status de cada etapa.",
    prioridade: "baixa",
    categoria: "neutro",
  };
}

/**
 * Resolve onde o operador deve ir ao clicar em “Continuar fluxo”.
 * Única fonte de verdade: delega para `getProximaEtapa` (prioridade routeTo > anchorId).
 */
export function resolveContinuarFluxoPedido(
  input: PedidoOperacionalInput
): ContinuarFluxoResult {
  const proxima = getProximaEtapa(input);

  const estadoFinal = proxima.categoria === "concluido";

  if (proxima.routeTo) {
    return {
      destino: { tipo: "navigate", to: proxima.routeTo },
      botaoLabel: `Continuar fluxo — ${proxima.titulo}`,
      etapaTitulo: proxima.titulo,
      descricao: proxima.descricao,
      estadoFinal: false,
      podeNavegar: true,
    };
  }

  if (proxima.anchorId) {
    return {
      destino: { tipo: "anchor", anchorId: proxima.anchorId },
      botaoLabel: `Continuar fluxo — ${proxima.titulo}`,
      etapaTitulo: proxima.titulo,
      descricao: proxima.descricao,
      estadoFinal: false,
      podeNavegar: true,
    };
  }

  return {
    destino: { tipo: "nenhuma" },
    botaoLabel: estadoFinal ? "Fluxo encerrado" : "Sem próxima etapa automática",
    etapaTitulo: proxima.titulo,
    descricao: proxima.descricao,
    estadoFinal,
    podeNavegar: false,
  };
}

/**
 * Pedido em estado terminal do fluxo operacional: não deve haver atalhos nem mutações
 * de status/envio na tela mãe (entrega/retirada concluída, cancelado ou estornado).
 * Pagamentos seguem regras próprias por linha (podem exigir estorno mesmo após entrega).
 */
export function isPedidoOperacionalImutavel(input: PedidoOperacionalInput): boolean {
  const s = String(input.status ?? "").toUpperCase();
  if (s === "CANCELADO" || s === "DEVOLVIDO") return true;
  return getProximaEtapa(input).categoria === "concluido";
}

export function getAcoesContextuais(
  input: PedidoOperacionalInput
): AcaoContextual[] {
  const o = resolvePedidoOperacional(input);
  const acoes: AcaoContextual[] = [];

  if (isPedidoOperacionalImutavel(input)) {
    return acoes;
  }

  if (o.status === "AGUARDANDO_PAGAMENTO" || !o.pagamentoOk) {
    acoes.push({
      label: "Ir para pagamento",
      anchorId: "sec-pagamento",
      destaque: true,
    });
    return acoes;
  }

  if (o.tipoEntrega === "DIGITAL") {
    return acoes;
  }

  if (o.statusFaturamento === "PENDENTE" || o.statusFaturamento === "ERRO") {
    acoes.push({
      label: "Ir para faturamento",
      routeTo: input.pedidoId ? pedidoFaturamentoPath(input.pedidoId) : undefined,
      anchorId: input.pedidoId ? undefined : "sec-faturamento",
      destaque: true,
    });
  }

  if (
    o.statusFaturamento === "EMITIDO" &&
    (o.statusSeparacao === "PENDENTE" ||
      o.statusSeparacao === "EM_SEPARACAO" ||
      o.statusSeparacao === "DIVERGENCIA" ||
      o.statusSeparacao === "FALTA_ESTOQUE")
  ) {
    const rSep =
      input.pedidoId != null ? pedidoSeparacaoPath(input.pedidoId) : undefined;
    acoes.push({
      label: "Ir para separação",
      routeTo: rSep,
      anchorId: rSep ? undefined : "sec-separacao",
      destaque:
        o.statusSeparacao === "PENDENTE" || o.statusSeparacao === "EM_SEPARACAO",
    });
  }

  if (o.tipoEntrega === "ENTREGA" && o.statusSeparacao === "SEPARADO") {
    const rExp =
      input.pedidoId != null ? pedidoExpedicaoPath(input.pedidoId) : undefined;
    acoes.push({
      label: "Ir para expedição",
      routeTo: rExp,
      anchorId: rExp ? undefined : "sec-expedicao",
      destaque:
        o.statusLogistico === "PENDENTE" ||
        o.statusLogistico === "AGUARDANDO_ENVIO" ||
        o.statusLogistico === "ETIQUETA_GERADA",
    });
  }

  if (
    (o.tipoEntrega === "RETIRADA_LOJA" || o.tipoEntrega === "RETIRADA_EVENTO") &&
    o.statusSeparacao === "SEPARADO"
  ) {
    const rRet =
      input.pedidoId != null ? pedidoRetiradaPath(input.pedidoId) : undefined;
    acoes.push({
      label: "Ir para retirada",
      routeTo: rRet,
      anchorId: rRet ? undefined : "sec-retirada",
      destaque:
        o.statusRetirada === "PENDENTE" ||
        o.statusRetirada === "PRONTO_PARA_RETIRADA",
    });
  }

  return acoes;
}

export function buildTimelineOperacional(
  input: PedidoOperacionalInput
): TimelineOperacionalItem[] {
  const o = resolvePedidoOperacional(input);
  const raw: Omit<TimelineOperacionalItem, "atual">[] = [];

  raw.push({
    id: "rec",
    titulo: "Pedido recebido",
    subtitulo: "Registro do pedido na loja",
    concluido: true,
    variante: "success",
  });

  const payDone =
    o.status !== "AGUARDANDO_PAGAMENTO" && o.pagamentoOk;
  raw.push({
    id: "pay",
    titulo: "Pagamento confirmado",
    subtitulo: payDone ? undefined : "Aguardando confirmação",
    concluido: payDone,
    variante: payDone ? "success" : "grey",
  });

  if (o.tipoEntrega === "DIGITAL") {
    const digDone = o.status === "ENTREGUE";
    raw.push({
      id: "dig",
      titulo: "Entrega digital",
      subtitulo: digDone
        ? "Download disponível ao cliente"
        : "Conclusão automática após pagamento",
      concluido: digDone,
      variante: digDone ? "success" : "grey",
    });

    let marcouAtualDig = false;
    return raw.map((it) => {
      const atual = !it.concluido && !marcouAtualDig;
      if (atual) marcouAtualDig = true;
      return { ...it, atual };
    });
  }

  const fatDone = o.statusFaturamento === "EMITIDO";
  raw.push({
    id: "fat",
    titulo: "Faturamento",
    subtitulo:
      o.statusFaturamento === "ERRO"
        ? "Erro ao emitir"
        : fatDone
          ? "Emitido"
          : "Pendente",
    concluido: fatDone,
    variante:
      o.statusFaturamento === "ERRO"
        ? "warning"
        : fatDone
          ? "success"
          : "grey",
  });

  const sepDone = o.statusSeparacao === "SEPARADO";
  raw.push({
    id: "sep",
    titulo: "Separação",
    subtitulo: o.statusSeparacao === "EM_SEPARACAO"
      ? "Em separação"
      : o.statusSeparacao === "DIVERGENCIA"
        ? "Divergência"
        : o.statusSeparacao === "FALTA_ESTOQUE"
          ? "Falta de estoque"
          : sepDone
            ? "Finalizada"
            : "Pendente",
    concluido: sepDone,
    variante:
      o.statusSeparacao === "DIVERGENCIA" || o.statusSeparacao === "FALTA_ESTOQUE"
        ? "warning"
        : sepDone
          ? "success"
          : "grey",
  });

  if (o.tipoEntrega === "ENTREGA") {
    const logDone =
      o.statusLogistico === "ENTREGUE" || o.status === "ENTREGUE";
    raw.push({
      id: "log",
      titulo: "Logística / entrega",
      subtitulo:
        o.statusLogistico === "ENTREGUE"
          ? "Entregue"
          : o.statusLogistico === "POSTADO" || o.statusLogistico === "EM_TRANSITO"
            ? "Em trânsito"
            : "Etiqueta / envio",
      concluido: logDone,
      variante:
        o.statusLogistico === "DEVOLVIDO" || o.statusLogistico === "EXTRAVIADO"
          ? "warning"
          : logDone
            ? "success"
            : "grey",
    });
  } else {
    const ret = o.statusRetirada;
    const retDone = ret === "RETIRADO" || ret === "ENTREGUE_NO_EVENTO";
    raw.push({
      id: "ret",
      titulo:
        o.tipoEntrega === "RETIRADA_EVENTO"
          ? "Retirada no evento"
          : "Retirada na loja",
      subtitulo:
        ret === "PRONTO_PARA_RETIRADA"
          ? "Pronto para retirada"
          : retDone
            ? "Concluído"
            : "Pendente",
      concluido: retDone,
      variante: retDone ? "success" : "grey",
    });
  }

  let marcouAtual = false;
  return raw.map((it) => {
    const atual = !it.concluido && !marcouAtual;
    if (atual) marcouAtual = true;
    return { ...it, atual };
  });
}

/** Monta a entrada dos utilitários a partir do detalhe gerencial + lista de pagamentos. */
export function buildPedidoOperacionalInput(
  detalhe: Record<string, unknown> & {
    status?: string;
    tipoEntrega?: string | null;
    statusFaturamento?: string | null;
    statusSeparacao?: string | null;
    statusLogistico?: string | null;
    statusRetirada?: string | null;
    enderecoEntregaId?: number | null;
    enderecoEntrega?: unknown;
  },
  pagamentos: Array<{ status?: string }>
): PedidoOperacionalInput {
  return {
    status: String(detalhe.status ?? ""),
    pedidoId: typeof detalhe.id === "string" ? detalhe.id : undefined,
    tipoEntrega: detalhe.tipoEntrega ?? undefined,
    statusFaturamento: detalhe.statusFaturamento ?? undefined,
    statusSeparacao: detalhe.statusSeparacao ?? undefined,
    statusLogistico: detalhe.statusLogistico ?? undefined,
    statusRetirada: detalhe.statusRetirada ?? undefined,
    pagamentos,
    enderecoEntrega: detalhe.enderecoEntrega,
    enderecoEntregaId: detalhe.enderecoEntregaId ?? null,
  };
}

export function scrollToAnchor(anchorId: string) {
  const el = document.getElementById(anchorId);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

/** Executa o destino retornado por `resolveContinuarFluxoPedido` (evita duplicar navigate/scroll na UI). */
export function executarContinuarFluxoDestino(
  destino: ContinuarFluxoDestino,
  navigate: (to: string) => void
): void {
  if (destino.tipo === "navigate") {
    navigate(destino.to);
    return;
  }
  if (destino.tipo === "anchor") {
    scrollToAnchor(destino.anchorId);
  }
}

export function labelTipoEntrega(t: TipoEntrega | string): string {
  const m: Record<string, string> = {
    ENTREGA: "Entrega (envio)",
    RETIRADA_LOJA: "Retirada na loja",
    RETIRADA_EVENTO: "Retirada no evento",
    DIGITAL: "Produto digital (download)",
  };
  return m[String(t)] ?? String(t);
}

export function labelStatusFaturamento(s: StatusFaturamento | string): string {
  const m: Record<string, string> = {
    PENDENTE: "Pendente",
    EMITIDO: "Emitido",
    CANCELADO: "Cancelado",
    ERRO: "Erro",
  };
  return m[String(s)] ?? String(s);
}

export function labelStatusSeparacao(s: StatusSeparacao | string): string {
  const m: Record<string, string> = {
    PENDENTE: "Pendente",
    EM_SEPARACAO: "Em separação",
    SEPARADO: "Separado",
    DIVERGENCIA: "Divergência",
    FALTA_ESTOQUE: "Falta de estoque",
    CANCELADA: "Cancelada",
  };
  return m[String(s)] ?? String(s);
}

export function labelStatusLogistico(s: StatusLogistico | string): string {
  const m: Record<string, string> = {
    PENDENTE: "Pendente",
    AGUARDANDO_ENVIO: "Aguardando envio",
    ETIQUETA_GERADA: "Etiqueta gerada",
    POSTADO: "Postado",
    EM_TRANSITO: "Em trânsito",
    ENTREGUE: "Entregue",
    DEVOLVIDO: "Devolvido",
    EXTRAVIADO: "Extraviado",
  };
  return m[String(s)] ?? String(s);
}

export function labelStatusRetirada(s: StatusRetirada | string): string {
  const m: Record<string, string> = {
    PENDENTE: "Pendente",
    PRONTO_PARA_RETIRADA: "Pronto para retirada",
    RETIRADO: "Retirado",
    ENTREGUE_NO_EVENTO: "Entregue no evento",
    NAO_RETIRADO: "Não retirado",
    CANCELADA: "Cancelada",
  };
  return m[String(s)] ?? String(s);
}
